import type { Asset } from './asset';
import type { SubAsset } from './sub-asset';

// Tile server (3D Tiles) metadata, stored in its own table (1:1 with DataUpload)
export type Tileset = Partial<{
	id: string;
	dataUploadId: string;
	// Tile server tileset id (UUID string per the v1 API)
	externalId: string | null;
	url: string | null;
	// queued | processing | ready | error (mirrors the tile server status enum)
	status: string | null;
	// Current/last pipeline stage reported by the tile server
	stage: string | null;
	// Failure message (+ log tail) reported by the tile server when status = error
	errorMessage: string | null;
	lon: number | null;
	lat: number | null;
	height: number | null;
	heading: number | null;
	anchor: string | null;
	// True once a real location has been applied (guards the converter's
	// auto-extracted georeference from overwriting a user's placement).
	positioned: boolean;
	// Conversion pipeline and source extension.
	type: string | null;
	sourceFormat: string | null;
	// S3 prefix the tiles are published under; the CDN URL is built from it.
	slug: string | null;
	// Per-stage timings of the finished run.
	metrics: TilesetMetrics | null;
	// When the run was dispatched and when it reached a terminal state.
	startedAt: string | null;
	finishedAt: string | null;
	// Computed at read time, present only while the conversion is running.
	progressView: TilesetProgressView | null;
	createdAt: string;
	updatedAt: string;
}>;

/**
 * Live progress of a running conversion. Computed by the backend on every read
 * rather than stored, because percent and the estimate are functions of now.
 */
export type TilesetProgressView = {
	percent: number;
	elapsedSeconds: number;
	/**
	 * Null for the first few percent of a run, where extrapolating from elapsed
	 * time is noise — a confident wrong number is worse than saying nothing.
	 */
	estimatedRemainingSeconds: number | null;
	/** Tile files finished out of tile files the run will produce, when known. */
	work: { done: number; total: number } | null;
};

export type TilesetMetrics = {
	steps?: { stage: string; seconds: number }[];
	input_bytes?: number;
	output_bytes?: number;
	total_seconds?: number;
};

export type DataUpload = Partial<{
	id: string;
	name: string | null;
	uploadType: DataUploadType;
	subSurfaceMode: boolean | null;
	cesiumId: string | null;
	fileUrl: string | null;
	fileType: string | null;
	fileSizeInKB: number | null;
	// Tile server (3D Tiles) metadata (own table), present when uploadType = TILESET
	tileset: Tileset | null;
	assetId: string | null;
	subAssetId: string | null;
	inspectionId: string | null;
	damageId: string | null;
	createdAt: string;
	updatedAt: string;
	deletedAt: string | null;
	createRequestId: string | null;
	updateRequestId: string | null;
	createdById: string | null;
	updatedById: string | null;

	asset: Asset | null;
	subAsset: SubAsset | null;
}>;

export const DataUploadType = {
	FILE: 'FILE',
	CESIUM_ID: 'CESIUM_ID',
	TILESET: 'TILESET',
} as const;

// Types we accept for upload (mirror backend CreateTilesetDto):
// `3d-model` -> .glb, `cad` -> .dxf, `bim` -> .ifc.
export const TILESET_TYPES = ['3d-model', 'cad', 'bim'] as const;
export const TILESET_ANCHORS = ['base', 'center', 'origin'] as const;
// Gaussian splat (.ply/.splat) source type + spherical-harmonics (sh) options
export const SPLAT_TYPE = 'gaussian-splat' as const;
export const SPLAT_SH_OPTIONS = ['full', 'none'] as const;
export type TilesetGlbType = (typeof TILESET_TYPES)[number];
export type TilesetType = TilesetGlbType | typeof SPLAT_TYPE;
export type TilesetAnchor = (typeof TILESET_ANCHORS)[number];
export type TilesetShOption = (typeof SPLAT_SH_OPTIONS)[number];

// Accepted source file extensions per tile server type (see server swagger).
// Planned: '.dwg' (cad) — add here, in TILESET_TYPES and in getTilesetTypeForFileName together.
export const TILESET_ACCEPTED_EXTENSIONS = ['.glb', '.ply', '.splat', '.dxf', '.ifc'] as const;

/**
 * Maps a source file name to its tile server `type` (the API takes the same
 * multipart endpoint for all of them; only `type` + the file differ).
 * Returns null for unsupported extensions.
 */
export function getTilesetTypeForFileName(fileName: string): TilesetType | null {
	const lower = fileName.toLowerCase();
	if (lower.endsWith('.glb')) return '3d-model';
	if (lower.endsWith('.dxf')) return 'cad';
	if (lower.endsWith('.ifc')) return 'bim';
	// Planned — uncomment together with TILESET_TYPES / TILESET_ACCEPTED_EXTENSIONS:
	// if (lower.endsWith('.dwg')) return 'cad';
	if (lower.endsWith('.ply') || lower.endsWith('.splat')) return SPLAT_TYPE;
	return null;
}

export type DataUploadType = (typeof DataUploadType)[keyof typeof DataUploadType];
